import { isXmlFile } from '../XmlViewer';
import { getFileExtension } from '../../lib/fileTypes';

/**
 * What kind of preview a file gets — the single source of truth for two
 * separate questions that used to be answered independently:
 *
 *   Preview   — which renderer to mount
 *   Workspace — whether to mount a preview surface at all
 *
 * Resolution is extension-first for markup because `file.type` is stamped once
 * at creation (FileBrowser.js) and never revisited. A file named `foo.xml`
 * created or uploaded normally is typed `other`, and the old
 * `file.type === 'doc' && isXmlFile(name)` gate sent it to "No preview
 * available" even though XmlViewer handles it perfectly. It also makes the
 * Markdown⇄XML switch robust: that switch only renames, it never touches
 * `type`.
 */

export const PREVIEW_KIND = {
    MARKDOWN: 'markdown',
    XML: 'xml',
    HTML: 'html',
    MOCKUP: 'mockup',
    ASSET: 'asset',
    NONE: 'none',
};

const MARKDOWN_EXTENSIONS = new Set(['md', 'markdown', 'mdx', 'txt']);
const HTML_EXTENSIONS = new Set(['html', 'htm']);

export function getPreviewKind(file) {
    if (!file) return PREVIEW_KIND.NONE;

    // Before isXmlFile: `.svg` is both valid XML and an image, and an image is
    // the more useful of the two readings.
    if (file.type === 'asset') return PREVIEW_KIND.ASSET;
    if (isXmlFile(file.name)) return PREVIEW_KIND.XML;
    // After `mockup` so an explicitly-typed mockup that happens to be named
    // `.html` keeps its Sandpack renderer, but before the `doc` branch below:
    // Chat.js classifies uploaded `.html` as `doc`, which would otherwise send
    // a whole web page through the markdown renderer.
    if (file.type === 'mockup') return PREVIEW_KIND.MOCKUP;
    if (HTML_EXTENSIONS.has(getFileExtension(file.name))) return PREVIEW_KIND.HTML;
    if (file.type === 'doc' || MARKDOWN_EXTENSIONS.has(getFileExtension(file.name))) {
        return PREVIEW_KIND.MARKDOWN;
    }

    return PREVIEW_KIND.NONE;
}

/**
 * Text-only documents reflow, so every viewport-shaped control — device
 * presets, zoom, reload, zen — is inert for them and gets swapped out for a
 * toolbar of things that actually apply.
 */
export const isTextDocKind = (kind) =>
    kind === PREVIEW_KIND.MARKDOWN || kind === PREVIEW_KIND.XML;

export const hasPreview = (file) => getPreviewKind(file) !== PREVIEW_KIND.NONE;
